import type {
  SignupAnswer,
  SignupQuestionConfig,
  SignupQuestionOption,
} from '@workspace/api-backend'
import { cn } from '@workspace/ui/lib/utils'
import { useMemo, useState } from 'react'
import { questionTitle } from '../lib/use-signup-question.js'

interface SignupQuestionCardProps {
  config: SignupQuestionConfig
  audience: 'founder' | 'investor'
  /** Called with the latest answer on every change. No optionKey means skipped. */
  onChange: (answer: SignupAnswer) => void
}

const TILE =
  'rounded-lg border px-3 py-2 text-left text-xs leading-snug transition-colors disabled:opacity-40'
const TILE_IDLE = 'border-border bg-card/30 text-foreground hover:border-muted-foreground/30'
const TILE_ON = 'border-[#f97316] bg-[#f97316]/10 text-foreground'
const CHIP = 'rounded-full border px-2.5 py-1 text-xs transition-colors'
const INPUT =
  'flex h-9 w-full rounded-md border border-border bg-card px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-[#f97316] focus:outline-none focus:ring-1 focus:ring-[#f97316]'

/**
 * "How did you first hear about SeedForge?" — shown under the progress list
 * while the profile is analysed. The question, its options and follow-ups come
 * from Admin → Sign-up question. Nothing is saved here: the parent saves the
 * latest answer (or a skip) when the person presses Continue.
 */
export function SignupQuestionCard({ config, audience, onChange }: SignupQuestionCardProps) {
  // Arranged once per mount, so the shuffled order doesn't change mid-answer.
  const options = useMemo(() => arrangeOptions(config, audience), [config, audience])
  const [answer, setAnswer] = useState<SignupAnswer>({ audience })
  const [skipped, setSkipped] = useState(false)

  const update = (next: SignupAnswer) => {
    setAnswer(next)
    onChange(next)
  }

  if (skipped) return null

  const question = questionTitle(config, audience)
  const labelOf = (option: SignupQuestionOption) =>
    (audience === 'investor' && option.investorLabel) || option.label
  const selected = options.find((option) => option.key === answer.optionKey)
  const showAiQuestion = config.aiQuestion.enabled && selected && !selected.aiIntroduced

  const choose = (option: SignupQuestionOption, position: number) =>
    update({
      audience,
      question,
      optionKey: option.key,
      optionLabel: labelOf(option),
      position,
      ...(option.followUp.type !== 'none' && { followUpPrompt: option.followUp.prompt }),
      ...(option.aiIntroduced && { aiRole: 'introduced' as const }),
      ...(!option.aiIntroduced &&
        config.aiQuestion.enabled && { aiQuestion: config.aiQuestion.text }),
    })

  const setFollowUp = (value: string) =>
    update({
      ...answer,
      detail: value || undefined,
      // On the AI option, a picked assistant ("Which one?") is also stored as aiTool.
      ...(selected?.aiIntroduced &&
        selected.followUp.type === 'choices' && { aiTool: value || undefined }),
    })

  return (
    <div className="mt-8 text-left">
      <p className="text-sm font-medium text-foreground">{question}</p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {options.map((option, position) => (
          <button
            key={option.key}
            type="button"
            onClick={() => choose(option, position)}
            className={cn(TILE, option.key === answer.optionKey ? TILE_ON : TILE_IDLE)}
          >
            {labelOf(option)}
          </button>
        ))}
      </div>

      {selected?.followUp.type === 'text' && (
        <input
          type="text"
          maxLength={200}
          value={answer.detail ?? ''}
          onChange={(e) => setFollowUp(e.target.value)}
          placeholder={selected.followUp.prompt}
          className={cn(INPUT, 'mt-3')}
        />
      )}

      {selected?.followUp.type === 'choices' && (
        <Chips
          prompt={selected.followUp.prompt}
          choices={selected.followUp.choices}
          value={answer.detail}
          onPick={setFollowUp}
        />
      )}

      {showAiQuestion && (
        <div className="mt-4">
          <p className="text-xs text-muted-foreground">{config.aiQuestion.text}</p>
          <div className="mt-2 flex gap-2">
            {(['checked', 'none'] as const).map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => update({ ...answer, aiRole: role, aiTool: undefined })}
                className={cn(CHIP, answer.aiRole === role ? TILE_ON : TILE_IDLE)}
              >
                {role === 'checked' ? config.aiQuestion.yesLabel : config.aiQuestion.noLabel}
              </button>
            ))}
          </div>
          {answer.aiRole === 'checked' && config.aiQuestion.choices.length > 0 && (
            <Chips
              choices={config.aiQuestion.choices}
              value={answer.aiTool}
              onPick={(tool) => update({ ...answer, aiTool: tool })}
            />
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setSkipped(true)
          onChange({ audience, question })
        }}
        className="mt-4 text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        Skip
      </button>
    </div>
  )
}

function Chips({
  prompt,
  choices,
  value,
  onPick,
}: {
  prompt?: string
  choices: string[]
  value: string | undefined
  onPick: (choice: string) => void
}) {
  return (
    <div className="mt-3">
      {prompt ? <p className="text-xs text-muted-foreground">{prompt}</p> : null}
      <div className="mt-2 flex flex-wrap gap-2">
        {choices.map((choice) => (
          <button
            key={choice}
            type="button"
            onClick={() => onPick(choice)}
            className={cn(CHIP, value === choice ? TILE_ON : TILE_IDLE)}
          >
            {choice}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * The options this person sees: their audience only, shuffled when the
 * question says so (so no option wins by sitting first), pinned ones last.
 */
export function arrangeOptions(
  config: SignupQuestionConfig,
  audience: 'founder' | 'investor'
): SignupQuestionOption[] {
  const visible = config.options.filter(
    (option) => option.audience === 'both' || option.audience === audience
  )
  const rest = visible.filter((option) => !option.pinLast)
  const ordered = config.shuffle
    ? rest
        .map((option) => ({ option, sortKey: Math.random() }))
        .sort((a, b) => a.sortKey - b.sortKey)
        .map(({ option }) => option)
    : rest
  return [...ordered, ...visible.filter((option) => option.pinLast)]
}
